import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

const AnimatedTitle = ({ animate = true }) => {
  const styles = `
    .title-text {
      font-family: 'Playfair Display', serif;
      font-weight: 700;
      fill: #F6D365;
      filter: drop-shadow(0 0 25px rgba(246, 211, 101, 0.4));
      letter-spacing: 0.02em;
    }
    
    .sub-title-text {
      font-family: 'Cormorant Garamond', serif;
      font-weight: 600;
      fill: #FF9A8B;
      filter: drop-shadow(0 0 20px rgba(255, 154, 139, 0.3));
      letter-spacing: 0.04em;
    }
    
    .tagline-text {
      font-family: 'Montserrat', sans-serif;
      font-weight: 500;
      fill: #8E9AAF;
      filter: drop-shadow(0 0 15px rgba(142, 154, 175, 0.3));
      letter-spacing: 0.08em;
    }
    
    .jp-text {
      font-family: 'Noto Serif JP', serif;
      font-weight: 500;
      fill: #FF9A8B;
      filter: drop-shadow(0 0 12px rgba(255, 154, 139, 0.3));
    }

    .title-container {
      position: relative;
      overflow: hidden;
    }

    .bg-gradient {
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, 
        rgba(246,211,101,0.1) 0%,
        rgba(255,154,139,0.15) 50%,
        rgba(74,144,226,0.2) 100%
      );
      pointer-events: none;
      z-index: 0;
    }

    .light-rays {
      position: absolute;
      inset: 0;
      background: radial-gradient(
        circle at 50% 50%,
        rgba(246,211,101,0.2) 0%,
        rgba(246,211,101,0) 70%
      );
      pointer-events: none;
      z-index: 1;
      animation: pulse 4s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 0.8; }
    }
  `;

  const mainRef = useRef(null);

  useEffect(() => {
    if (!animate || !mainRef.current) return;

    const mainTitle = mainRef.current.querySelector('.main-title');
    const subTitle = mainRef.current.querySelector('.sub-title');
    const tagline = mainRef.current.querySelector('.tagline');
    const jpText = mainRef.current.querySelectorAll('.jp-text');

    gsap.set([mainTitle, subTitle, tagline, jpText], {
      opacity: 0
    });

    gsap.set(mainTitle, { y: -50 });
    gsap.set(subTitle, { y: 30 });
    gsap.set(tagline, { y: 20 });
    gsap.set(jpText, { y: 15 });

    const tl = gsap.timeline();

    tl.to(mainTitle, {
      y: 0,
      opacity: 1,
      duration: 2.5,
      ease: 'power3.out',
    });

    tl.to(subTitle, {
      y: 0,
      opacity: 1,
      duration: 2,
      ease: 'power2.out',
    }, '-=2');

    tl.to(tagline, {
      y: 0,
      opacity: 1,
      duration: 2,
      ease: 'power2.out',
    }, '-=1.8');

    jpText.forEach((el, index) => {
      tl.to(el, {
        y: 0,
        opacity: 1,
        duration: 1.6,
        ease: 'power2.out',
      }, `-=${1.4}`);
    });

    const addGlow = () => {
      gsap.to(mainTitle, {
        filter: 'drop-shadow(0 0 30px rgba(246, 211, 101, 0.6))',
        duration: 3,
        repeat: -1,
        yoyo: true,
      });

      gsap.to(subTitle, {
        filter: 'drop-shadow(0 0 25px rgba(255, 154, 139, 0.5))',
        duration: 3.3,
        repeat: -1,
        yoyo: true,
        delay: 0.5
      });

      gsap.to(tagline, {
        filter: 'drop-shadow(0 0 20px rgba(142, 154, 175, 0.5))',
        duration: 3.6,
        repeat: -1,
        yoyo: true,
        delay: 1
      });

      jpText.forEach((el, index) => {
        gsap.to(el, {
          filter: 'drop-shadow(0 0 18px rgba(255, 154, 139, 0.4))',
          duration: 3,
          repeat: -1,
          yoyo: true,
          delay: 0.4 * index
        });
      });
    };

    tl.call(addGlow);
  }, [animate]);

  return (
    <div className="w-[400px] h-[200px] relative">
      <div className="bg-gradient" />
      <div className="light-rays" />
      <style>{styles}</style>
      <svg ref={mainRef} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 200" className="w-full h-full pointer-events-none relative z-10">
        <defs>
          <style type="text/css">
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Cormorant+Garamond:wght@600&family=Montserrat:wght@500&family=Noto+Serif+JP:wght@500&display=swap');
          </style>
        </defs>

        <g transform="translate(200,100)" textAnchor="middle">
          <text
            className="title-text animate-el main-title"
            x="0"
            y="-30"
            fontSize="56"
          >
            Almost Lovers
          </text>

          <text
            className="sub-title-text animate-el sub-title"
            x="0"
            y="10"
            fontSize="32"
          >
            A Story of Connection
          </text>

          <text
            className="tagline-text animate-el tagline"
            x="0"
            y="40"
            fontSize="20"
          >
            Between Paradise and Memory
          </text>

          {[
            { text: '愛', x: -45 },
            { text: 'と', x: -15 },
            { text: '記', x: 15 },
            { text: '憶', x: 45 }
          ].map((item, index) => (
            <text
              key={index}
              className="jp-text animate-el"
              x={item.x}
              y="65"
              fontSize="16"
            >
              {item.text}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
};

export default AnimatedTitle;
